import { SideModal, Button } from "shared";
import SVG from "shared/SVG";
import { SVG_ENUM } from "enums";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import JSZip from "jszip";
import { useGetMe } from "hooks/auth";
import "./AssetDetailsSideModal.scss";

interface AssetDetailsSideModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: any | null;
  slug: string;
  investmentTypeName?: string;
  isOwn?: boolean;
  hidePriceHeader?: boolean;
  onViewTransactions?: () => void;
  isCompanyDetailsView?: boolean;
  onCloseAsset?: () => void;
}

const INTEREST_TYPE_LABELS: Record<string, string> = {
  fixed: "Stałe",
  variable: "Zmienne",
  periodically_fixed: "Okresowo-stałe",
};

const LOAN_TYPE_LABELS: Record<string, string> = {
  borrowed: "Zaciągnięta",
  lent: "Udzielona",
};

const PAYMENT_LABELS: Record<string, string> = {
  monthly: "Miesięczne",
  quarterly: "Kwartalne",
  yearly: "Roczne",
  at_end_of_year: "Na koniec roku",
  at_end: "Na koniec roku",
};

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  apartment: "Mieszkanie",
  house: "Dom",
  land: "Działka",
  other: "Inne",
};

const SUBTITLE_MAP: Record<string, string> = {
  investment_policy: "Aktywo: Polisy inwestycyjne / IKE",
  treasury_bonds: "Aktywo: Obligacje skarbowe",
  artwork: "Aktywo: Dzieło sztuki",
  precious_stones: "Aktywo: Kamienie szlachetne",
  precious_metals: "Aktywo: Metale szlachetne",
  real_estate: "Aktywo: Nieruchomości",
  loans: "Aktywo: Pożyczki",
  currencies: "Aktywo: Obce waluty",
  crypto: "Aktywo: Tokeny/Kryptowaluty",
};

const METAL_TYPE_LABELS: Record<string, string> = {
  gold: "Złoto",
  silver: "Srebro",
};

const getPreciousMetalName = (item: any): string => {
  const metalType = item?.details?.metalType;
  if (!metalType) return item?.name || "Metale szlachetne";
  if (metalType === "other")
    return item?.details?.metalTypeOther || item?.name || "Inne";
  return METAL_TYPE_LABELS[metalType] ?? metalType;
};

const formatDate = (dateString: any): string => {
  if (!dateString) return "Brak";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Brak";
  return date.toLocaleDateString("pl-PL");
};

const formatPrice = (item: any): string => {
  if (!item?.primaryValue) return "Brak";
  const num = Number(item.primaryValue);
  const formatted = num
    .toLocaleString("en-US", {
      minimumFractionDigits: Number.isInteger(num) ? 0 : 2,
      maximumFractionDigits: 2,
    })
    .replace(/,/g, "\u00A0");
  const currency = item.currency?.currency ?? "";
  return currency ? `${formatted} ${currency}` : formatted;
};

const formatSalePrice = (item: any): string | null => {
  if (item?.saleValue == null) return null;
  const saleNum = Number(item.saleValue);
  const formatted = saleNum
    .toLocaleString("en-US", {
      minimumFractionDigits: Number.isInteger(saleNum) ? 0 : 2,
      maximumFractionDigits: 2,
    })
    .replace(/,/g, "\u00A0");
  const currency = item?.currency?.currency ?? "";
  return currency ? `${formatted} ${currency}` : formatted;
};

const formatUserSalePrice = (item: any): string | null => {
  if (item?.userSaleValue == null) return null;
  const num = Number(item.userSaleValue);
  const formatted = num
    .toLocaleString("en-US", {
      minimumFractionDigits: Number.isInteger(num) ? 0 : 2,
      maximumFractionDigits: 2,
    })
    .replace(/,/g, "\u00A0");
  const currency = item?.currency?.currency ?? "";
  return currency ? `${formatted} ${currency}` : formatted;
};

const getDurationText = (
  item: any,
  indefiniteText = "Bezterminowo",
): string => {
  if (!item) return "Brak";
  const durationDate = item.details?.durationDate;
  if (durationDate === null || durationDate === undefined)
    return indefiniteText;
  if (typeof durationDate === "string" && durationDate !== "") {
    const d = new Date(durationDate);
    if (isNaN(d.getTime())) return "Brak";
    if (d.getFullYear() === 2000 && d.getMonth() === 0 && d.getDate() === 1)
      return "Brak";
    return d.toLocaleDateString("pl-PL");
  }
  return "Brak";
};

const formatNumericWithSpaces = (value: any): string | undefined => {
  if (value == null || value === "") return undefined;
  const str = String(value);
  const dotIndex = str.indexOf(".");
  const intPart = dotIndex >= 0 ? str.slice(0, dotIndex) : str;
  let decPart = dotIndex >= 0 ? str.slice(dotIndex) : "";
  if (!/^\d+$/.test(intPart)) return str;
  if (decPart.length === 2) decPart += "0";
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0");
  return formattedInt + decPart;
};

const Field = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => (
  <div className="asset-details__field">
    <span className="asset-details__label">{label}</span>
    <span className="asset-details__value">
      {value != null && value !== "" ? value : "Brak"}
    </span>
  </div>
);

const CarouselArrowIcon = () => (
  <svg
    width="5"
    height="8"
    viewBox="0 0 5 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.87695 0.779297L0.778561 3.91127L3.87695 7.04325"
      stroke="#151515"
      strokeWidth="1.55757"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PhotoCarousel = ({
  photoFiles,
  currentIndex,
  onPrev,
  onNext,
  onDotClick,
}: {
  photoFiles: any[];
  currentIndex: number;
  onPrev: () => void;
  onNext: () => void;
  onDotClick: (i: number) => void;
}) => (
  <div className="asset-details__field asset-details__field--photos">
    <span className="asset-details__label">
      Załączone zdjęcia ({photoFiles.length})
    </span>
    {photoFiles.length > 0 ? (
      <div className="asset-details__carousel">
        <div className="asset-details__carousel-image-wrapper">
          <img
            src={photoFiles[currentIndex]?.file?.url}
            alt={`Zdjęcie ${currentIndex + 1}`}
            className="asset-details__carousel-image"
          />
          {photoFiles.length > 1 && (
            <>
              <button
                className="asset-details__carousel-arrow asset-details__carousel-arrow--prev"
                onClick={onPrev}
              >
                <CarouselArrowIcon />
              </button>
              <button
                className="asset-details__carousel-arrow asset-details__carousel-arrow--next"
                onClick={onNext}
              >
                <CarouselArrowIcon />
              </button>
            </>
          )}
        </div>
        {photoFiles.length > 1 && (
          <div className="asset-details__carousel-dots">
            {photoFiles.map((_: any, index: number) => (
              <button
                key={index}
                className={`asset-details__carousel-dot${
                  index === currentIndex
                    ? " asset-details__carousel-dot--active"
                    : ""
                }`}
                onClick={() => onDotClick(index)}
              />
            ))}
          </div>
        )}
      </div>
    ) : (
      <span className="asset-details__value">Brak</span>
    )}
  </div>
);

const FilesSection = ({ files, label }: { files: any[]; label: string }) => {
  const [isZipping, setIsZipping] = useState(false);

  const handleDownloadAll = async () => {
    if (files.length === 1) {
      const link = document.createElement("a");
      link.href = files[0].file.url;
      link.download = files[0].file.name;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    setIsZipping(true);
    try {
      const zip = new JSZip();
      await Promise.all(
        files.map(async (file: any) => {
          const response = await fetch(file.file.url);
          const blob = await response.blob();
          zip.file(file.file.name, blob);
        }),
      );
      const content = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(content);
      const link = document.createElement("a");
      link.href = url;
      link.download = "umowy.zip";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setIsZipping(false);
    }
  };

  return (
    <div className="asset-details__field asset-details__field--files">
      <div className="asset-details__files-header">
        <span className="asset-details__label">{label}</span>
        {files.length > 0 && (
          <button
            className="asset-details__download-all"
            onClick={handleDownloadAll}
            disabled={isZipping}
          >
            {isZipping ? "Pakowanie..." : "Pobierz wszystko"}
            <SVG type={SVG_ENUM.FILE_DOWNLOAD} />
          </button>
        )}
      </div>
      {files.length === 0 ? (
        <span className="asset-details__value">Brak</span>
      ) : (
        <div className="asset-details__files-list">
          {files.map((file: any) => (
            <div key={file.file.uuid} className="asset-details__file-item">
              <span className="asset-details__file-name">{file.file.name}</span>
              <a
                href={file.file.url}
                download={file.file.name}
                target="_blank"
                rel="noreferrer"
                className="asset-details__file-download"
              >
                <SVG type={SVG_ENUM.FILE_DOWNLOAD} />
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AssetDetailsSideModal = ({
  isOpen,
  onClose,
  item,
  slug,
  investmentTypeName,
  isOwn = false,
  hidePriceHeader = false,
  onViewTransactions,
  isCompanyDetailsView = false,
  onCloseAsset,
}: AssetDetailsSideModalProps) => {
  const { data: authorizedUser } = useGetMe();
  const isAdmin = authorizedUser?.role === "admin";
  const isRelianceClientView = !isAdmin && !isOwn;
  const hideFinancials = isRelianceClientView && isCompanyDetailsView;
  const isRelianceOwnView = isRelianceClientView && !isCompanyDetailsView;

  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  useEffect(() => {
    setCurrentPhotoIndex(0);
  }, [item?.uuid]);

  const photoFiles = (item?.files ?? []).filter((f: any) => f.role === "photo");
  const contractFiles = (item?.files ?? []).filter(
    (f: any) =>
      f.role === "contract" ||
      f.role === "closure_contract" ||
      f.role === "sale_contract",
  );
  const allFiles = (item?.files ?? []).filter(
    (f: any) =>
      f.role === "contract" ||
      f.role === "attachment" ||
      f.role === "closure_contract" ||
      f.role === "sale_contract",
  );

  const isKnownSlug = slug in SUBTITLE_MAP;
  const isOtherAsset =
    !isKnownSlug &&
    !["stocks", "shares", "crypto", "currencies"].includes(slug);
  const subtitle = SUBTITLE_MAP[slug] ?? "Aktywo: Inne";

  const priceFormatted = formatPrice(item);

  const formatValueForDisplay = (rawValue: any): string => {
    if (rawValue == null) return "Brak";
    const num = Number(rawValue);
    if (isNaN(num)) return "Brak";
    const formatted = num
      .toLocaleString("en-US", {
        minimumFractionDigits: Number.isInteger(num) ? 0 : 2,
        maximumFractionDigits: 2,
      })
      .replace(/,/g, "\u00A0");
    const currency = item?.currency?.currency ?? "";
    return currency ? `${formatted} ${currency}` : formatted;
  };

  const priceDisplayFormatted = isRelianceOwnView
    ? formatValueForDisplay(item?.saleValue)
    : priceFormatted;

  const getPriceHeader = (): { label: string; value: string } | null => {
    if (slug === "investment_policy") return null;
    if (slug === "loans") {
      if (hideFinancials) return null;
      const rawValue = isRelianceOwnView ? item?.saleValue : item?.primaryValue;
      const currency = item?.currency?.currency;
      const num = Number(rawValue);
      const formattedAmount =
        rawValue != null
          ? `${num
              .toLocaleString("en-US", {
                minimumFractionDigits: Number.isInteger(num) ? 0 : 2,
                maximumFractionDigits: 2,
              })
              .replace(/,/g, "\u00A0")}${currency ? ` ${currency}` : ""}`
          : "Brak";
      return { label: "Kwota:", value: formattedAmount };
    }
    if (hideFinancials) return null;
    if (slug === "treasury_bonds") {
      return { label: "Koszt nabycia:", value: priceDisplayFormatted };
    }
    if (!isKnownSlug) {
      return { label: "Wartość:", value: priceDisplayFormatted };
    }
    return { label: "Cena zakupu:", value: priceDisplayFormatted };
  };

  const priceHeader = getPriceHeader();
  const showPhotos = ["artwork", "real_estate"].includes(slug) || !isKnownSlug;
  const displayedFiles = !isKnownSlug ? allFiles : contractFiles;
  const filesLabel = !isKnownSlug
    ? `Załączone pliki (${allFiles.length})`
    : "Umowa";

  const renderFieldsInner = () => {
    switch (slug) {
      case "currencies":
        return (
          <div className="company-details-table">
            <h2 className="side-modal__table-heading">Informacje o walucie</h2>
            <table className="side-modal__table">
              <tbody>
                {!hideFinancials && (
                  <tr>
                    <td className="side-modal__table-label">Nazwa:</td>
                    <td className="side-modal__table-cell">
                      {item?.name || item?.currency?.name || "-"}
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="side-modal__table-label">Waluta:</td>
                  <td className="side-modal__table-cell">
                    {item?.currency?.currency || "-"}
                  </td>
                </tr>
                {item?.additionalInfo && (
                  <tr>
                    <td className="side-modal__table-label">
                      Dodatkowe informacje:
                    </td>
                    <td className="side-modal__table-cell">
                      {item.additionalInfo}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );

      case "crypto":
        return (
          <div className="company-details-table">
            <h2 className="side-modal__table-heading">Informacje o tokenie</h2>
            <table className="side-modal__table">
              <tbody>
                <tr>
                  <td className="side-modal__table-label">Nazwa:</td>
                  <td className="side-modal__table-cell">
                    {item?.name || "-"}
                  </td>
                </tr>
                <tr>
                  <td className="side-modal__table-label">Waluta:</td>
                  <td className="side-modal__table-cell">
                    {item?.currency?.currency || "-"}
                  </td>
                </tr>
                {item?.additionalInfo && (
                  <tr>
                    <td className="side-modal__table-label">
                      Dodatkowe informacje:
                    </td>
                    <td className="side-modal__table-cell">
                      {item.additionalInfo}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );

      case "investment_policy":
        return (
          <>
            <Field label="Nazwa" value={item?.name} />
            <Field label="Waluta" value={item?.currency?.currency} />
            <Field
              label="Data zawarcia"
              value={formatDate(item?.details?.contractDate)}
            />
            <Field
              label="Czas trwania"
              value={getDurationText(item, "Bezterminowa")}
            />
            <Field
              label="Składki"
              value={
                PAYMENT_LABELS[item?.details?.premiumPayment] ??
                item?.details?.premiumPayment
              }
            />
            <Field label="Dodatkowe informacje" value={item?.additionalInfo} />
            {item?.archivedAt && (
              <Field
                label="Data zamknięcia"
                value={formatDate(item.archivedAt)}
              />
            )}
          </>
        );

      case "treasury_bonds": {
        const interestRateType =
          item?.details?.interestRateType ?? item?.details?.interestType;
        return (
          <>
            <Field
              label="Rodzaj oprocentowania"
              value={INTEREST_TYPE_LABELS[interestRateType] ?? interestRateType}
            />
            <Field
              label="Ilość sztuk"
              value={item?.quantity != null ? String(item.quantity) : undefined}
            />
            <Field
              label="Wysokość oprocentowania"
              value={
                item?.details?.interestRate != null &&
                item?.details?.interestRate !== ""
                  ? `${item.details.interestRate}%`
                  : undefined
              }
            />
            <Field label="Dodatkowe informacje" value={item?.additionalInfo} />
            {item?.archivedAt && (
              <Field
                label="Data zamknięcia"
                value={formatDate(item.archivedAt)}
              />
            )}
          </>
        );
      }

      case "artwork":
        return (
          <>
            <Field label="Rodzaj" value={item?.details?.artworkType} />
            {!hideFinancials && (
              <Field label="Cena zakupu" value={priceDisplayFormatted} />
            )}
            {isAdmin && item?.saleValue != null && (
              <Field
                label="Cena sprzedaży"
                value={formatSalePrice(item) ?? ""}
              />
            )}
            {!isAdmin &&
              isOwn &&
              !!item?.archivedAt &&
              item?.userSaleValue != null && (
                <Field
                  label="Cena sprzedaży"
                  value={formatUserSalePrice(item) ?? ""}
                />
              )}
            {isRelianceClientView &&
              !!item?.archivedAt &&
              item?.userSaleValue != null && (
                <Field
                  label="Cena sprzedaży"
                  value={formatUserSalePrice(item) ?? ""}
                />
              )}
            {!hideFinancials && (
              <Field label="Waluta" value={item?.currency?.currency} />
            )}
            <Field label="Tytuł" value={item?.name} />
            <Field label="Autor" value={item?.details?.author} />
            <Field label="Epoka/wiek" value={item?.details?.eraAge} />
            <Field label="Dodatkowe informacje" value={item?.additionalInfo} />
            {item?.archivedAt && (
              <Field
                label="Data sprzedaży"
                value={formatDate(item.archivedAt)}
              />
            )}
          </>
        );

      case "precious_stones":
        return (
          <>
            <Field label="Nazwa" value={item?.name} />
            {!hideFinancials && (
              <Field label="Cena zakupu" value={priceDisplayFormatted} />
            )}
            {isAdmin && item?.saleValue != null && (
              <Field
                label="Cena sprzedaży"
                value={formatSalePrice(item) ?? ""}
              />
            )}
            {!isAdmin &&
              isOwn &&
              !!item?.archivedAt &&
              item?.userSaleValue != null && (
                <Field
                  label="Cena sprzedaży"
                  value={formatUserSalePrice(item) ?? ""}
                />
              )}
            {isRelianceClientView &&
              !!item?.archivedAt &&
              item?.userSaleValue != null && (
                <Field
                  label="Cena sprzedaży"
                  value={formatUserSalePrice(item) ?? ""}
                />
              )}
            {!hideFinancials && (
              <Field label="Waluta" value={item?.currency?.currency} />
            )}
            <Field
              label="Masa"
              value={
                item?.details?.weight != null
                  ? `${formatNumericWithSpaces(item.details.weight)} g`
                  : undefined
              }
            />
            <Field label="Szlif" value={item?.details?.cut} />
            <Field label="Barwa" value={item?.details?.color} />
            <Field label="Czystość" value={item?.details?.clarity} />
            <Field label="Dodatkowe informacje" value={item?.additionalInfo} />
          </>
        );

      case "precious_metals":
        return (
          <>
            <Field label="Nazwa" value={getPreciousMetalName(item)} />
            {!hideFinancials && (
              <Field label="Cena zakupu" value={priceDisplayFormatted} />
            )}
            {isAdmin && item?.saleValue != null && (
              <Field
                label="Cena sprzedaży"
                value={formatSalePrice(item) ?? ""}
              />
            )}
            {!isAdmin &&
              isOwn &&
              !!item?.archivedAt &&
              item?.userSaleValue != null && (
                <Field
                  label="Cena sprzedaży"
                  value={formatUserSalePrice(item) ?? ""}
                />
              )}
            {isRelianceClientView &&
              !!item?.archivedAt &&
              item?.userSaleValue != null && (
                <Field
                  label="Cena sprzedaży"
                  value={formatUserSalePrice(item) ?? ""}
                />
              )}
            {!hideFinancials && (
              <Field label="Waluta" value={item?.currency?.currency} />
            )}
            <Field
              label="Masa"
              value={
                item?.details?.weight != null
                  ? `${formatNumericWithSpaces(item.details.weight)} g`
                  : undefined
              }
            />
            <Field
              label="Próba"
              value={formatNumericWithSpaces(item?.details?.fineness)}
            />
            <Field label="Dodatkowe informacje" value={item?.additionalInfo} />
            {item?.archivedAt && (
              <Field
                label="Data zamknięcia"
                value={formatDate(item.archivedAt)}
              />
            )}
          </>
        );

      case "real_estate": {
        const propertyType = item?.details?.propertyType;
        const propertyTypeLabel =
          propertyType === "other"
            ? item?.details?.propertyTypeOther || "Inne"
            : PROPERTY_TYPE_LABELS[propertyType] ?? propertyType;
        const area = item?.details?.area;
        const areaDisplay =
          area != null && area !== ""
            ? `${Number(area).toLocaleString("pl-PL")} m²`
            : undefined;
        const priceSqm = (() => {
          const a = Number(area);
          const rawPrice = isRelianceOwnView
            ? item?.saleValue
            : item?.primaryValue;
          const p = Number(rawPrice);
          if (!a || !p || a === 0) return undefined;
          const currency = item?.currency?.currency ?? "";
          const formatted = (p / a).toLocaleString("pl-PL", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          });
          return currency ? `${formatted} ${currency}` : formatted;
        })();
        return (
          <>
            <Field label="Typ nieruchomości" value={propertyTypeLabel} />
            {!hideFinancials && (
              <Field label="Cena zakupu" value={priceDisplayFormatted} />
            )}
            {isAdmin && item?.saleValue != null && (
              <Field
                label="Cena sprzedaży"
                value={formatSalePrice(item) ?? ""}
              />
            )}
            {!isAdmin &&
              isOwn &&
              !!item?.archivedAt &&
              item?.userSaleValue != null && (
                <Field
                  label="Cena sprzedaży"
                  value={formatUserSalePrice(item) ?? ""}
                />
              )}
            {isRelianceClientView &&
              !!item?.archivedAt &&
              item?.userSaleValue != null && (
                <Field
                  label="Cena sprzedaży"
                  value={formatUserSalePrice(item) ?? ""}
                />
              )}
            {!hideFinancials && (
              <Field label="Waluta" value={item?.currency?.currency} />
            )}
            <Field label="Miejscowość" value={item?.details?.city} />
            <Field label="Kod pocztowy" value={item?.details?.postalCode} />
            <Field label="Ulica i numer" value={item?.details?.address} />
            <Field label="Powierzchnia" value={areaDisplay} />
            <Field label="Cena za m²" value={priceSqm} />
            <Field label="Dodatkowe informacje" value={item?.additionalInfo} />
            {item?.archivedAt && (
              <Field
                label="Data sprzedaży"
                value={formatDate(item.archivedAt)}
              />
            )}
          </>
        );
      }

      case "loans": {
        const loanType = item?.loanType ?? item?.details?.loanType;
        const interestRate = item?.details?.interestRate;
        const interestRateType =
          item?.details?.interestRateType ?? item?.details?.interestType;
        const interestPayment = item?.details?.interestPayment;
        const autoRenewal = item?.details?.autoRenewal;
        return (
          <>
            <Field
              label="Podmiot"
              value={item?.entity ?? item?.details?.entity}
            />
            {!hideFinancials && (
              <>
                <Field
                  label="Rodzaj"
                  value={LOAN_TYPE_LABELS[loanType] ?? loanType}
                />
                <Field
                  label="Wysokość oprocentowania"
                  value={
                    interestRate != null && interestRate !== ""
                      ? `${interestRate}%`
                      : undefined
                  }
                />
                <Field
                  label="Rodzaj oprocentowania"
                  value={
                    INTEREST_TYPE_LABELS[interestRateType] ?? interestRateType
                  }
                />
                <Field
                  label="Odsetki"
                  value={PAYMENT_LABELS[interestPayment] ?? interestPayment}
                />
                <Field
                  label="Data zawarcia"
                  value={formatDate(item?.details?.contractDate)}
                />
                <Field label="Czas trwania" value={getDurationText(item)} />
                <Field
                  label="Automatyczne przedłużenie"
                  value={
                    autoRenewal === true || autoRenewal === 1
                      ? "Tak"
                      : autoRenewal === false || autoRenewal === 0
                      ? "Nie"
                      : undefined
                  }
                />
              </>
            )}
            <Field label="Dodatkowe informacje" value={item?.additionalInfo} />
            {!hideFinancials && item?.archivedAt && (
              <Field
                label="Data zamknięcia"
                value={formatDate(item.archivedAt)}
              />
            )}
          </>
        );
      }

      default:
        return (
          <>
            <Field label="Nazwa" value={item?.name} />
            {!hideFinancials && (
              <Field label="Cena zakupu" value={priceDisplayFormatted} />
            )}
            {!(hideFinancials && isOtherAsset) && (
              <Field label="Waluta" value={item?.currency?.currency} />
            )}
            <Field label="Dodatkowe informacje" value={item?.additionalInfo} />
            {isAdmin && item?.saleValue != null && (
              <Field
                label="Cena sprzedaży"
                value={formatSalePrice(item) ?? ""}
              />
            )}
            {isRelianceClientView &&
              !!item?.archivedAt &&
              item?.userSaleValue != null && (
                <Field
                  label="Cena sprzedaży"
                  value={formatUserSalePrice(item) ?? ""}
                />
              )}
            {item?.archivedAt && (
              <Field
                label="Data sprzedaży"
                value={formatDate(item.archivedAt)}
              />
            )}
          </>
        );
    }
  };

  const clientName = (item as any)?.owner?.name;
  const subtitleNode = (
    <>
      {isAdmin && clientName ? (
        <p>
          {subtitle} &nbsp;|&nbsp; <b>Klient:</b> {clientName}
        </p>
      ) : (
        <p>{subtitle}</p>
      )}
      {priceHeader && !hidePriceHeader && (
        <p className="asset-details__price-counter">
          {priceHeader.label} <b>{priceHeader.value}</b>
        </p>
      )}
    </>
  );

  return createPortal(
    <SideModal
      isOpen={isOpen}
      onClose={onClose}
      title="Szczegółowe informacje"
      subtitle={subtitleNode}
      className={`side-modal--asset-details side-modal--asset-details-${slug}`}
    >
      {onCloseAsset && !item?.archivedAt && (
        <Button
          className="button--rounded asset-details__close-asset-btn"
          onClick={onCloseAsset}
          label="Zamknij aktywo"
        />
      )}
      <div className="asset-details">
        {renderFieldsInner()}

        {onViewTransactions && (
          <Button
            className="button--rounded asset-details__transactions-btn"
            onClick={onViewTransactions}
            label="Lista transakcji"
          />
        )}

        {isAdmin && item?.status === "sold" && item?.owner && (
          <>
            <Field
              label="Sprzedane klientowi"
              value={`${item.owner.name} (ID: ${item.owner.id})`}
            />
          </>
        )}

        {showPhotos && (
          <PhotoCarousel
            photoFiles={photoFiles}
            currentIndex={currentPhotoIndex}
            onPrev={() =>
              setCurrentPhotoIndex((prev) =>
                prev === 0 ? photoFiles.length - 1 : prev - 1,
              )
            }
            onNext={() =>
              setCurrentPhotoIndex((prev) =>
                prev === photoFiles.length - 1 ? 0 : prev + 1,
              )
            }
            onDotClick={setCurrentPhotoIndex}
          />
        )}

        {!(
          hideFinancials &&
          (slug === "loans" || slug === "currencies" || slug === "crypto")
        ) && <FilesSection files={displayedFiles} label={filesLabel} />}
      </div>
    </SideModal>,
    document.body,
  );
};

export default AssetDetailsSideModal;
